import { useAuth } from '../providers/AuthContext';
// import styles from './Admin.module.css';
import { Base, NoTopbar } from '../layouts/Base';
import { Outlet, useLocation, useParams} from 'react-router-dom';
import {
    useQuery,
    useQueryClient,
  } from '@tanstack/react-query';
import LoginWidget from '../widgets/LoginWidget';
import api from '../API';
import loadingGif from '../assets/Circles-menu-3.gif';
import ProjectJoinGate from '../pages/projects/ProjectJoinGate';
import { useDataManagerAccess } from '../hooks/useDataManagerAccess';

function makeLayout(useBaseLayout, content){
    return useBaseLayout ? (<Base content={content}></Base>) : content
}

function AdminTest(){
    return (<>
        <div style={{position:'fixed',top:'50%',left:'50%',transform:'translate(-50%, -50%)'}}>
            <h2>Admin page testing</h2>
        </div>
    </>)
}
function UserTest(){
    return (<>
        <div style={{position:'fixed',top:'50%',left:'50%',transform:'translate(-50%, -50%)'}}>
            <h2>User page testing</h2>
        </div>
    </>)
}
function PublicTest(){
    return (<>
        <div style={{position:'fixed',top:'50%',left:'50%',transform:'translate(-50%, -50%)'}}>
            <h2>Public page testing</h2>
        </div>
    </>)
}

function AccessControlLayout({isAdmin, isPublic, useBaseLayout, checkParam, requireDataManagerOrAdmin}){
    
    // set up hooks
    const { user, token, pending } = useAuth();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { isDataManager, pending: dataManagerPending, refreshAccess } = useDataManagerAccess();
    
    const params = useParams();
    const checks = {
        'fileId':`file/${params.fileId}`,
        'folderId':`folder/${params.folderId}`,

        'collectionId':`collection/${params.collectionId}`,
        'userId':`user/${params.userId}`,
        'groupId':`group/${params.groupId}`,
        'itemId': `item/${params.itemId}`
    }

    // If checkParam is explicitly specified, use only that param for the access
    // control check (ignoring any other id params present in the route). This
    // supports routes with multiple id params (e.g. /projects/:folderId/view/:itemId)
    // where one param is the primary resource to check and the rest are navigation
    // parameters read directly by the page component.
    const presentIds = Object.entries(checks).filter(([key]) => params[key]);
    let apiCalls;
    if (checkParam) {
        const checkEntry = checks[checkParam];
        apiCalls = checkEntry && params[checkParam] ? [checkEntry] : [];
    } else {
        apiCalls = presentIds.map(([, value]) => value);
    }

    const { data: response, isLoading, isError, error } = useQuery({ 
        queryKey: [`${apiCalls[0]} ${token}`], 
        queryFn: () => {
            return api.get(apiCalls[0])
        },
        enabled: !!(apiCalls.length===1 && !isPublic)
    });
    
    // if this access control layout is created with isPublic flag, just return the outlet
    if(isPublic){
        return makeLayout(useBaseLayout, <Outlet></Outlet>)
    }

    // If multiple id params are present but no checkParam was specified, we don't
    // know which resource to use for the permission check. Add a checkParam prop
    // to the AccessControlLayout for this route to resolve the ambiguity.
    // Example: <AccessControlLayout checkParam="itemId" />
    if(!checkParam && presentIds.length > 1){
        return makeLayout(useBaseLayout, <>
        <div style={{position:'fixed',top:'50%',left:'50%',transform:'translate(-50%, -50%)'}}>
            Route configuration error: multiple id parameters found ({presentIds.map(([k]) => k).join(', ')}) but no <code>checkParam</code> was specified on AccessControlLayout. Add <code>checkParam="paramName"</code> to identify which resource to use for the access control check.
        </div>
        </>)
    }

    // don't show additional info if we are still loading either the user info or the permissions for the id
    if(pending || isLoading){
        return makeLayout(useBaseLayout, <img src={loadingGif} style={{position:'fixed',top:'50%',left:'50%',transform:'translate(-50%, -50%)'}}></img>)
    }

    // if no id parameters are being used to test permissions, just make sure the user is logged in 
    if(apiCalls.length === 0 && !user){
        return (<>
        <LoginWidget alwaysOpen={true} title={isAdmin ? 'Log in as an administrator to view this page' : 'Log in to view this page.'}></LoginWidget>
        </>);
    
    }

    // There was an error with the api call
    if(isError){
        return makeLayout(useBaseLayout, <>
        <div style={{position:'fixed',top:'50%',left:'50%',transform:'translate(-50%, -50%)'}}>
            Oops, there was an error. {JSON.stringify(error)}
        </div>
        </>)
    }
    
    // if this layout was created with the isAdmin flag, make sure the user is an admin
    if(isAdmin && (!user || !user.admin)){
        return makeLayout(useBaseLayout, <>
        <div style={{textAlign:'center'}}>
            <h2>Permission denied.</h2>
            <div>You must be logged in as an administrator to access this page.</div>
            <LoginWidget alwaysOpen={true}></LoginWidget>
        </div>
        </>);
    }

    if (requireDataManagerOrAdmin) {
        if (dataManagerPending) {
            return makeLayout(useBaseLayout, <>
                <div style={{position:'fixed',top:'50%',left:'50%',transform:'translate(-50%, -50%)'}}>
                    <img src={loadingGif} alt="loading"/>
                </div>
            </>);
        }
        if (!user || !isDataManager) {
            return makeLayout(useBaseLayout, <>
            <div style={{textAlign:'center'}}>
                <h2>Permission denied.</h2>
                <div>You must be a Data Manager or administrator to access this page.</div>
                {user && (
                    <p style={{marginTop: '1em'}}>
                        <button type="button" onClick={() => refreshAccess()}>
                            Check again
                        </button>
                        <span style={{display: 'block', marginTop: '0.5em', color: '#586069', fontSize: '0.9em'}}>
                            If an administrator just added you to Data Managers, click to re-check access.
                        </span>
                    </p>
                )}
                <LoginWidget alwaysOpen={true}></LoginWidget>
            </div>
            </>);
        }
    }

    // status 400 means no resource was found with this id
    if(response?.status==400){
        return (<>
        <div style={{textAlign:'center'}}><h2>Not found.</h2></div>
        </>);
    }
    // status 403 means the user does not have permissions to access this resource
    if(response?.status==403){
        const isProjectFolderCheck = checkParam === 'folderId' || (!checkParam && !!params.folderId);
        const isProjectPath = location.pathname.startsWith('/projects/');
        if (user && isProjectFolderCheck && isProjectPath && params.folderId) {
            const queryKey = [`${apiCalls[0]} ${token}`];
            return makeLayout(true, <ProjectJoinGate
                folderId={params.folderId}
                onRedeemed={() => queryClient.invalidateQueries({ queryKey })}
            />);
        }
        return makeLayout(true, <>
        <div style={{textAlign:'center'}}>
            <h2>Permission denied.</h2>
            <div>Please request permission from your administrator, or log in as a different user.</div>
            <LoginWidget alwaysOpen={true}></LoginWidget>
        </div>
        </>);
    }
    // status 401 means the api call was made without a (valid) token so it looks like the user is not logged in
    if(response?.status==401){
        return makeLayout(false, <>
        <div style={{textAlign:'center'}}>
            <h2>You must be logged in to access this page.</h2>
            <LoginWidget alwaysOpen={true}></LoginWidget>
        </div>
        </>);
    }
    // deal with any other statuses other than success (200)
    if (response && response.status!==200) {
        return makeLayout(useBaseLayout, 
            <>
                <div style={{textAlign:'center'}}><h2>Error {response.status}</h2></div>
                <pre>{JSON.stringify(response.data, null, 2)}</pre>
            </>
        )
    } else {
        // Ok, everything succeeded - return the outlet.
        return makeLayout(useBaseLayout, <>
        <Outlet/>
        </>);
    }
    
}

export default AccessControlLayout;
export { AdminTest, UserTest, PublicTest };
